import { useCallback, useRef } from 'react'

import { AudioStream, VideoStream } from '@nativewaves/nw-cloud-sdk/content'
import { useSnackbar } from 'notistack'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { StreamEdit } from 'domains/Workspaces/Streams'
import { StreamGeneralFormValues } from 'domains/Workspaces/Streams/General/types'

const toFormValues = (stream: VideoStream | AudioStream): StreamGeneralFormValues => ({
  isAutodelete: !!stream.contentLifecyclePolicy.deleteAfter,
  contentLifecyclePolicy: { deleteAfter: stream.contentLifecyclePolicy.deleteAfter ?? 0 },
  avcTaskAssociations: stream.avcTaskAssociations.map(({ taskId, enabled }) => ({ taskId, enabled })),
})

/** `isAutodelete` off means "no expiration", which the API expresses as 0. */
const resolveDeleteAfter = (values: StreamGeneralFormValues) => {
  const deleteAfter = values.contentLifecyclePolicy.deleteAfter
  // `DurationField` emits NaN for an emptied input, which would serialize to null.
  return values.isAutodelete && Number.isFinite(deleteAfter) ? deleteAfter : 0
}

export type UseStreamGeneralFormProps = {
  stream: VideoStream | AudioStream
  save: (edit: StreamEdit, options?: { onSuccess?: () => void; onError?: () => void }) => void
  onSaved?: () => void
}

/**
 * One form covering every editable stream property, published in a single PATCH.
 *
 * Two deliberate properties:
 * - Defaults are captured once at mount and never reset from the server. The form is only
 *   mounted in edit mode, so there is no effect that could wipe in-progress edits.
 * - The payload carries only fields that actually changed, compared against those captured
 *   defaults. That keeps a concurrent rename via the page header — or an edit to an untouched
 *   field from the Config tab — from being clobbered, and makes it structurally impossible to
 *   send `contentSettings` (whose `Set`-typed members are arrays at runtime and would
 *   serialize to `{}`).
 */
export const useStreamGeneralForm = ({ stream, save, onSaved }: UseStreamGeneralFormProps) => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const defaultsRef = useRef(toFormValues(stream))

  const formMethods = useForm<StreamGeneralFormValues>({ defaultValues: defaultsRef.current })

  const buildEdit = useCallback((values: StreamGeneralFormValues): StreamEdit => {
    const defaults = defaultsRef.current
    const edit: StreamEdit = {}

    const associations = values.avcTaskAssociations.map(({ taskId, enabled }) => ({ taskId, enabled }))
    if (JSON.stringify(associations) !== JSON.stringify(defaults.avcTaskAssociations)) {
      edit.avcTaskAssociations = associations
    }

    const deleteAfter = resolveDeleteAfter(values)
    if (deleteAfter !== resolveDeleteAfter(defaults)) {
      edit.contentLifecyclePolicy = { deleteAfter }
    }

    return edit
  }, [])

  /**
   * Drives the warning and the extra confirmation step. Only true when saving would newly
   * schedule content for deletion — relaxing or removing a policy destroys nothing.
   */
  const nextDeleteAfter = resolveDeleteAfter(formMethods.watch())
  const willScheduleDeletion = nextDeleteAfter > 0 && nextDeleteAfter !== resolveDeleteAfter(defaultsRef.current)

  const handleSubmit = formMethods.handleSubmit((values) => {
    const edit = buildEdit(values)

    if (!Object.keys(edit).length) {
      onSaved?.()
      return
    }

    save(edit, {
      onSuccess: () => {
        defaultsRef.current = values
        formMethods.reset(values, { keepSubmitCount: true })
        enqueueSnackbar(t('domain:Workspace.Streams.saved'), { variant: 'success' })
        onSaved?.()
      },
      onError: () => enqueueSnackbar(t('domain:Workspace.Streams.saveError'), { variant: 'error' }),
    })
  })

  return { formMethods, handleSubmit, willScheduleDeletion }
}
